import type {
  BaseRecord,
  CrudSort,
  DataProvider,
  GetListParams,
  LogicalFilter,
  Pagination,
} from "@refinedev/core";
import { config } from "../config/config";
import { axiosInstance } from "../utils/axios";

export const API_URL = config.BASE_URL + "/api/v1";

type ListResponse<T> = {
  responseData: T[];
  pageMetadata: {
    count: number;
  };
};

const getPaginationParams = (pagination: Pagination | undefined) => {
  const params = new URLSearchParams();
  params.append("page", ((pagination?.current ?? 1) - 1)?.toString());
  params.append("size", pagination?.pageSize?.toString() ?? "");
  return params;
};

const getSortersParams = (sorters: CrudSort[] | undefined) => {
  const params = new URLSearchParams();
  const sorter = sorters && sorters?.length > 0 ? sorters[0] : null;
  if (sorter) {
    params.append("sort", sorter.field);
    params.append("order", sorter.order);
  }
  return params;
};

const getFiltersParams = (filters: LogicalFilter[] | undefined) => {
  const params = new URLSearchParams();
  if (filters) {
    for (const filter of filters) {
      const { field, value } = filter;
      if (value) {
        params.append(field, value);
      }
    }
  }
  return params;
};

const getQueriesParams = (
  queries: { field: string; value: string }[] | undefined
) => {
  const params = new URLSearchParams();
  if (queries) {
    for (const query of queries) {
      params.append(query.field, query.value);
    }
  }
  return params;
};

export const dataProvider: DataProvider = {
  getList: async <TData extends BaseRecord = BaseRecord>({
    resource,
    sorters,
    pagination,
    filters,
    meta,
  }: GetListParams) => {
    const searchParams = new URLSearchParams([
      ...getPaginationParams(pagination),
      ...getSortersParams(sorters),
      ...getFiltersParams(filters as LogicalFilter[]),
      ...getQueriesParams(meta?.queries),
    ]);

    const response = await axiosInstance.get<ListResponse<TData>>(
      `${API_URL}/${resource}?${searchParams.toString()}`
    );

    if (response.status < 200 || response.status > 299) throw response;

    return {
      data: response.data.responseData,
      total: response.data.pageMetadata.count,
    };
  },
  getMany: async () => {
    throw new Error("Not implemented");
  },
  getOne: async ({ resource, id }) => {
    const response = await axiosInstance.get(`/api/${resource}/${id}`);

    if (response.status < 200 || response.status > 299) throw response;

    return {
      data: response.data.responseData,
    };
  },
  create: async () => {
    throw new Error("Not implemented");
  },
  update: async () => {
    throw new Error("Not implemented");
  },
  getApiUrl: () => API_URL,
  deleteOne: () => {
    throw new Error("Not implemented");
  },
  custom: async ({ url, method, payload, headers, query, meta }) => {
    if (query || meta?.query) {
      const queryParams = new URLSearchParams(query ?? meta?.query);
      url += `?${queryParams.toString()}`;
    }

    const response = await axiosInstance.request({
      url: `${API_URL}/${url}`,
      method,
      headers,
      data: payload,
    });

    if (response.status < 200 || response.status > 299) throw response;

    if (
      response.headers &&
      response.headers["content-type"] === "text/csv;charset=UTF-8"
    ) {
      // If data is csv, download it
      const blob = new Blob([response.data]);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.style.display = "none";
      a.href = url;

      const contentDisposition = response.headers["content-disposition"];
      const fileNameMatch = contentDisposition.match(
        /filename\*?=["']?(?:UTF-8'')?([^"';]+)["']?/i
      );
      // filename static for now until we can get the filename from the response
      a.download = fileNameMatch[1] ?? "export.csv";

      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
    }

    return {
      data: response.data.responseData,
    };
  },
};
